/* eslint-disable */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useState, useEffect } from "react";
import { boxContainerFull } from "../../../shared/styles";
import { sendPhotoToS3 } from "../../../utils/helpers/uploadFileUtil";
import Thumbnail from "../../../shared/Thumbnail";

import UploadImages from "./upload/UploadImages";
import { useAtom } from "jotai";
import globalState from "../../../store";
import { getJpegFileFromHeic } from "../../photos/components/utils";
import {
  checkApplePatentImageExtension,
  getFileExtensionFromType,
} from "../../logo/components/utils";
import { ListManager } from "react-beautiful-dnd-grid";
import { IGalleryPhoto, IService } from "../../../types/types";
import { getMaxServicePhotosOrder, getOrderedServicePhotos } from "../utils";
import { buildS3MediaUrl } from "../../../utils/helpers/s3UrlHelpers";

interface ServiceImagesProps {
  thisService: IService;
  activeService: IService;
  reorderServiceImage: (sourceIndex: number, destinationIndex: number) => void;
}

const ServiceImages: React.FC<ServiceImagesProps> = ({
  thisService,
  activeService,
  reorderServiceImage,
}) => {
  const [globalStateAtom, setGlobalState] = useAtom(globalState);
  const [loading, setLoading] = useState(false);
  const [stockPhotosBool, setStockPhotosBool] = useState<boolean | null>(null);

  const servicePhotos = getOrderedServicePhotos(
    globalStateAtom?.gallery,
    thisService?.["service-name"]
  );

  async function onDropHandler(event: any) {
    setLoading(true);
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      await onChangeHandler(files);
    }
    setLoading(false);
  }

  const onChangeHandler = async (files: any) => {
    setLoading(true);
    for (let i = 0; i < files!.length; i++) {
      let curFile = files![i];
      if (/image\//.test(curFile?.type) === true) {
        const fileExtension = getFileExtensionFromType(curFile.type);
        if (checkApplePatentImageExtension(fileExtension)) {
          curFile = await getJpegFileFromHeic(curFile);
        }
      }
      const { fileUrl: pushedImageUrl } = await sendPhotoToS3(curFile);
      if (pushedImageUrl) {
        const maxAllPhotosOrder = globalStateAtom.gallery.reduce(
          (max, photo) => {
            return Math.max(photo.all_photos_order, max);
          },
          0
        );
        const maxServicePhotosOrder = getMaxServicePhotosOrder(
          globalStateAtom.gallery,
          thisService["service-name"]
        );
        const galleryPhoto: IGalleryPhoto = {
          photo: pushedImageUrl,
          all_photos_order: maxAllPhotosOrder + 1,
          gallery_order: -1,
          homepage_order: -1,
          gallery_name: "",
          galleries: [],
          associated_service: thisService["service-name"],
          alt_tag: `${thisService["service-name"]} image`,
          services: [
            {
              name: thisService["service-name"],
              order: maxServicePhotosOrder + 1,
            },
          ],
        };
        const newGallery = [galleryPhoto, ...globalStateAtom.gallery];
        setGlobalState({
          ...globalStateAtom,
          gallery: newGallery,
        });
      }
    }
    setLoading(false);
  };

  // this checks for stock photos to auto-set the 'Stock photos' checkbox based on if there are stock photos
  useEffect(() => {
    if (stockPhotosBool) {
      const stockPhotosAlreadyThere = globalStateAtom.gallery.find(
        (galleryPhoto) =>
          galleryPhoto?.stock &&
          galleryPhoto.services
            .map((service) => service.name)
            .includes(thisService["service-name"])
      );
      if (stockPhotosAlreadyThere) return;

      // add the stock photos back for this service
      const maxAllPhotosOrder = globalStateAtom.gallery.reduce((max, photo) => {
        return Math.max(photo.all_photos_order, max);
      }, 0);
      const maxServicePhotosOrder = getMaxServicePhotosOrder(
        globalStateAtom.gallery,
        thisService["service-name"]
      );
      const stockPhotos: IGalleryPhoto[] = [1, 2, 3].map((num) => ({
        all_photos_order: maxAllPhotosOrder + num,
        photo: buildS3MediaUrl(
          `${thisService["service-name"]
            .replace("&", "")
            .replace("%26", "")
            .toLocaleLowerCase()
            .split(" ")
            .join("-")}-${num}.jpeg`.replace("--", "-")
        ),
        gallery_order: -1,
        homepage_order: -1,
        gallery_name: "",
        galleries: [],
        associated_service: thisService["service-name"],
        alt_tag: `${thisService["service-name"]} image`,
        services: [
          {
            name: thisService["service-name"],
            order: maxServicePhotosOrder + num,
          },
        ],
        stock: true,
      }));
      setGlobalState({
        ...globalStateAtom,
        gallery: [...globalStateAtom.gallery, ...stockPhotos],
      });
    } else if (stockPhotosBool === false) {
      // remove stock photos
      const newGallery = globalStateAtom.gallery.filter((galleryPhoto) => {
        const shouldRemove =
          galleryPhoto?.stock &&
          galleryPhoto.services
            .map((service) => service.name)
            .includes(thisService["service-name"]);
        return !shouldRemove;
      });
      setGlobalState({ ...globalStateAtom, gallery: newGallery });
    }
  }, [stockPhotosBool]);

  const handleDelete = (photo: string) => {
    const newGallery = globalStateAtom.gallery.map((galleryPhoto) => {
      if (galleryPhoto.photo === photo) {
        return {
          ...galleryPhoto,
          services: galleryPhoto.services.filter(
            (service) => service.name !== thisService["service-name"]
          ),
        };
      }
      return galleryPhoto;
    });
    setGlobalState({ ...globalStateAtom, gallery: newGallery });
  };

  const deleteServicePhotos = () => {
    const newGallery = globalStateAtom.gallery.map((galleryPhoto) => {
      return {
        ...galleryPhoto,
        services: galleryPhoto.services.filter(
          (service) => service.name !== thisService["service-name"]
        ),
      };
    });
    setGlobalState({ ...globalStateAtom, gallery: newGallery });
  };

  return (
    <div className={`${boxContainerFull} mt-3`}>
      {loading && <img src="/loading.gif" width="20" alt="loading" />}
      <div className="flex flex-col items-center">
        <div className="h-32 w-full">
          <UploadImages
            onChangeHandler={onChangeHandler}
            onDropHandler={onDropHandler}
            activeService={activeService}
            multiple={true}
          />
        </div>
        <div className="max-h-96 overflow-y-scroll">
          <ListManager
            items={servicePhotos}
            direction="horizontal"
            maxItems={3}
            render={(photo) => {
              return (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={photo.photo}
                  className="w-36 flex-shrink-0 mt-3 mr-6"
                >
                  <Thumbnail
                    photo={photo}
                    handleDelete={handleDelete}
                    draggable={false}
                  />
                </div>
              );
            }}
            onDragEnd={reorderServiceImage}
          />
        </div>
        <label className="flex justify-center items-center">
          Stock photos
          <input
            type="checkbox"
            className="ml-3"
            onChange={(e) => setStockPhotosBool(!stockPhotosBool)}
            checked={!!stockPhotosBool}
          />
        </label>
        <label className="flex justify-center items-center mt-6">
          {/* Delete service photos */}
          <button
            // type="checkbox"
            className="ml-3"
            onClick={deleteServicePhotos}
          >
            Delete service photos
          </button>
        </label>
      </div>
    </div>
  );
};

export default ServiceImages;
